import { AnyAction, Dispatch } from 'redux'
import { ContentQuery, ContentService } from 'shared/services/ContentService'
import { assembleASPExperimentFlags } from 'shared/experiments/utils/assembleASPExperimentFlags'
import { getLogger } from 'shared/logger'
import { getGalleryNameSelector, getLocaleSelector, getMpvid, getProductVersion, getQuantitySelector, selectedOptionsSelector } from '~/app/store/config'
import { getContentCategories } from '~/app/store/content/actions'
import { taxonomyIdByCategoryIdSelector } from '~/app/store/filterCategories/reducer'
import { refinementIdsByFilterSelector } from '~/app/store/refinement/selectors'
import { MORE_LIKE_THIS_AMOUNT } from '~/app/store/similarTemplates/constants'
import {
  getASPExperimentFlags,
  getBypassApproval,
  getForcedRankingStrategyOverride,
  getNoCache,
  getRawSets,
  getRenderPropsOverrides,
  getSearchBackend,
  getTemplatePurposes,
  getUseConstraints,
} from '~/app/store/debug/reducer'
import { customColorVariationsSelector, designPersonalizationContextIdSelector } from '~/app/store/designPersonalization/selectors'
import { effectiveDesignCreationTypesSelector } from '~/app/store/designCreationTypes/selectors'
import {
  SIMILAR_TEMPLATES_ERROR,
  SIMILAR_TEMPLATES_DONE,
  SIMILAR_TEMPLATES_INJECT,
  SIMILAR_TEMPLATES_START,
  SIMILAR_TEMPLATES_RESET,
} from '~/app/store/constants'
import { getRawExperiments } from '~/app/store/experimentation'
import { byId } from '~/app/utils/byId'
import { buildTileEntity } from '~/app/store/buildContentState'
import {
  SimilarTemplatesErrorAction,
  SimilarTemplatesFetchAction,
  SimilarTemplatesFetchParams,
  SimilarTemplatesLoadingAction,
} from '~/app/store/similarTemplates/interface'
import { similarDesignByIdSelector, similarTemplatesByIdSelector } from '~/app/store/similarTemplates/reducer'
import { ThunkDispatch } from '~/app/store/interface'
import { IS_BROWSER } from '~/app/constants'
import { getGalleryType, getProductKey } from 'app/store/config/reducer'
import type { GalleryContentQuery } from 'shared/types/gallery/content-query'
import type { GalleryProductProductOptionsGroupsByProductKey } from 'shared/types/gallery/product'
import type { State } from 'shared/types/gallery/state'

const similarTemplatesLoading = (loading: boolean): SimilarTemplatesLoadingAction => ({
  type: SIMILAR_TEMPLATES_START,
  payload: loading,
})

const similarTemplatesFetch = ({ designs, tileEntities }: SimilarTemplatesFetchParams): SimilarTemplatesFetchAction => ({
  type: SIMILAR_TEMPLATES_DONE,
  payload: {
    designs,
    tileEntities,
  },
})

const similarTemplatesError = (error: Error): SimilarTemplatesErrorAction => ({
  type: SIMILAR_TEMPLATES_ERROR,
  payload: error,
})

export const fetchSimilarTemplates =
  (designId: string, productOptions: GalleryContentQuery.ProductOptions) =>
  async (dispatch: ThunkDispatch, getState: () => State.GlobalState): Promise<AnyAction> => {
    const state = getState()
    const rawExperiments = getRawExperiments(state)
    const experimentFlags = assembleASPExperimentFlags([], rawExperiments, getASPExperimentFlags(state))
    const refinements = refinementIdsByFilterSelector(state)
    const { categories, facetCategories } = getContentCategories(state)(refinements.categories)

    const designPersonalizationContextId = designPersonalizationContextIdSelector(state)
    const customColorVariations = customColorVariationsSelector(state)
    const searchBackend = getSearchBackend(state)
    const designCreationTypes = effectiveDesignCreationTypesSelector(state)

    const query: ContentQuery = {
      galleryType: getGalleryType(state),
      productKey: getProductKey(state),
      categories,
      facetCategories,
      designPersonalizationContextId,
      customColorVariations,
      bypassApproval: getBypassApproval(state),
      locale: getLocaleSelector(state),
      galleryName: getGalleryNameSelector(state),
      productVersion: getProductVersion(state),
      collection: refinements.collection,
      length: MORE_LIKE_THIS_AMOUNT,
      mpvId: getMpvid(state),
      selectedOptions: productOptions || selectedOptionsSelector(state),
      noCache: getNoCache(state),
      useConstraints: getUseConstraints(state),
      forcedRankingStrategyOverride: getForcedRankingStrategyOverride(state),
      renderProps: getRenderPropsOverrides(state),
      templatePurposes: getTemplatePurposes(state),
      searchBackend,
      experimentFlags,
      designCreationTypes,
      includeCategoryAndKeywordData: true,
      filterAltTextCompatibleTagging: true,
      moreLike: [designId],
      sets: getRawSets(state),
    }

    dispatch(similarTemplatesLoading(true))
    try {
      const { data: templatesData } = await ContentService.getContent(query)
      const getTaxonomyIdByCategoryId = taxonomyIdByCategoryIdSelector(state)
      const productOptionsByProductKeyMap: GalleryProductProductOptionsGroupsByProductKey = {}
      const quantity = getQuantitySelector(state)
      const designs: State.Design[] = []

      const tileEntities = templatesData.results.content.map(
        (tileEntity): State.TileEntity =>
          buildTileEntity({
            tileEntity,
            designs,
            productOptionsByProductKeyMap,
            locale: getLocaleSelector(state),
            quantity,
            getTaxonomyIdByCategoryId,
            isSimilarTemplate: true,
          }),
      )
      const designsById = byId<State.Design>(designs, 'designId')
      const tileEntitiesById = byId<State.TileEntity>(tileEntities, 'designId')

      return dispatch(
        similarTemplatesFetch({
          designs: designsById,
          tileEntities: tileEntitiesById,
        }),
      )
    } catch (e) {
      if (IS_BROWSER) {
        getLogger().error(e as Error)
        dispatch(similarTemplatesError(e as Error))
      }
      throw e
    }
  }

export const similarTemplateInjection =
  (startingEntity: string) =>
  async (dispatch: Dispatch, getState: () => State.GlobalState): Promise<AnyAction> => {
    const state = getState()

    const similarTemplates = similarTemplatesByIdSelector(state)
    const similarDesigns = similarDesignByIdSelector(state)

    const filteredSimilarTemplates = { [startingEntity]: similarTemplates(startingEntity) }
    const colorSwatchesId = filteredSimilarTemplates[startingEntity].colorSwatches

    const filteredSimilarDesigns = Object.fromEntries(colorSwatchesId.filter((id) => similarDesigns(id)).map((id) => [id, similarDesigns(id)]))

    return dispatch({
      type: SIMILAR_TEMPLATES_INJECT,
      payload: {
        designs: {
          byId: filteredSimilarDesigns,
        },
        tileEntities: {
          byId: filteredSimilarTemplates,
        },
      },
    })
  }

export const resetSimilarTemplates = () => ({
  type: SIMILAR_TEMPLATES_RESET,
})
